import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BrainCircuit,
  Code2,
  HeartPulse,
  Layers,
  Workflow,
  Database,
  Binary,
  Cpu,
  Rocket,
  ShieldCheck,
  ArrowRight,
  Gamepad2,
  MonitorSmartphone,
  Trophy,
  Volume2,
  Clapperboard,
} from "lucide-react";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import { FilmViewer } from "@/pages/idevra/VentureOverlay";

const AREA_ICONS = { BrainCircuit, Code2, HeartPulse, Layers, Workflow, Database, Binary, Cpu, Rocket, ShieldCheck };

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const rise = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

const InstallChip = () => {
  const [prompt, setPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIos(/iphone|ipad|ipod/i.test(navigator.userAgent) && !standalone);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (!prompt && !ios) return null;
  return (
    <button
      type="button"
      data-testid="install-app-btn"
      onClick={() => {
        sfx.clank();
        if (prompt) {
          prompt.prompt();
          prompt.userChoice.finally(() => setPrompt(null));
        } else {
          setIosHint((h) => !h);
        }
      }}
      className="iv-chip inline-flex items-center gap-2.5 !py-2.5 transition-colors hover:text-[#0284C7]"
    >
      <MonitorSmartphone size={15} className="shrink-0 text-[#0284C7]" />
      <span className="f-mono text-[9px] tracking-[0.2em] text-[#475569]">
        {iosHint ? "SAFARI: SHARE → ADD TO HOME SCREEN" : "INSTALL AS APP — IDEVRA ON YOUR PHONE"}
      </span>
    </button>
  );
};

export const HomePanel = () => {
  const [showFilm, setShowFilm] = useState(false);
  const toothrocket = IDEVRA.ventures.find((v) => v.slug === "toothrocket");
  return (
  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
    <motion.div variants={rise} className="iv-eyebrow">— FOUNDER-LED IP &amp; INNOVATION HOLDING</motion.div>
    <motion.div variants={rise} className="-mt-2 flex flex-col gap-3">
      <motion.img
        src={IDEVRA.brand.wordmark}
        alt="IDEVRA"
        className="iv-mark w-[280px] sm:w-[400px] lg:w-[480px] object-contain"
        data-testid="home-wordmark"
      />
      <button
        type="button"
        data-testid="pronounce-btn"
        title="Hear how to pronounce IDEVRA"
        onClick={() => {
          new Audio("/audio/idevra-pronounce.mp3").play().catch(() => {});
        }}
        className="group inline-flex w-fit items-center gap-2 rounded-full border border-[#CBD5E1] bg-white/50 px-3.5 py-1.5 transition-colors hover:border-[#38BDF8]"
      >
        <Volume2 size={12} className="text-[#0284C7] transition-transform group-hover:scale-110" />
        <span className="f-mono text-[9px] tracking-[0.3em] text-[#64748B] group-hover:text-[#0284C7]">
          I · DEV · RA
        </span>
      </button>
    </motion.div>
    <motion.h1 variants={rise} className="f-syncopate text-sm sm:text-base tracking-[0.35em] text-[#0284C7]">
      WHERE IDEAS BECOME INTELLECTUAL PROPERTY
    </motion.h1>
    <motion.p variants={rise} className="max-w-2xl text-sm sm:text-base leading-relaxed text-[#475569]">
      {IDEVRA.intro}
    </motion.p>
    <motion.div variants={rise} className="flex flex-wrap gap-3" data-testid="home-stats">
      <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">07</span> VENTURES</span>
      <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">10</span> CORE DOMAINS</span>
      <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">21</span> IP ASSETS</span>
      <span className="iv-chip"><span className="f-orbitron text-[#0284C7]">∞</span> IDEAS</span>
      <span className="iv-chip">FOUNDED BY <span className="text-[#0F172A]">SYED AMER</span></span>
    </motion.div>
    <motion.div variants={rise} className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        data-testid="home-film-btn"
        onClick={() => {
          sfx.clank();
          setShowFilm(true);
        }}
        className="group inline-flex items-center gap-3 rounded-full bg-[#0284C7] px-6 py-3 text-white shadow-[0_0_20px_rgba(56,189,248,0.35)] transition-colors hover:bg-[#0369A1]"
      >
        <Clapperboard size={15} className="transition-transform group-hover:scale-110" />
        <span className="f-syncopate text-[10px] tracking-[0.3em]">PREMIERE · TOOTHROCKET BRAND FILM</span>
      </button>
      <span className="f-mono text-[8px] tracking-[0.25em] text-[#94A3B8]">1:03 · PLAYS RIGHT HERE</span>
    </motion.div>
    <motion.p variants={rise} className="max-w-2xl border-l-2 border-[#38BDF8] pl-4 text-[13px] italic leading-relaxed text-[#64748B]">
      "{IDEVRA.principle}"
    </motion.p>
    <motion.div variants={rise} className="grid w-fit max-w-full grid-cols-1 gap-3" data-testid="controller-hint">
      <span className="iv-chip inline-flex items-center gap-2.5 !py-2.5">
        <Gamepad2 size={15} className="shrink-0 text-[#0284C7]" />
        <span className="f-mono text-[9px] tracking-[0.2em] text-[#475569]">
          OPTION: PLUG IN YOUR PLAYSTATION OR XBOX CONTROLLER — DRIVE THIS CONSOLE LIKE A GAME
        </span>
      </span>
      <InstallChip />
      <span
        className="iv-chip group inline-flex items-center gap-2.5 !py-2.5"
        data-testid="rewards-hint"
        title="Some operators still remember 1986…"
      >
        <Trophy size={15} className="shrink-0 text-[#D97706]" />
        <span className="f-mono text-[9px] tracking-[0.2em] text-[#475569]">
          REWARDS SYSTEM ACTIVE: EARN TROPHIES AS YOU EXPLORE — SECRET CODES AWAIT THE CURIOUS
        </span>
        <span
          className="f-mono text-[9px] tracking-[0.3em] text-[#0284C7] opacity-0 transition-opacity duration-700 group-hover:opacity-70"
          data-testid="konami-hint"
          aria-hidden="true"
        >
          ↑↑↓↓←→←→?A
        </span>
      </span>
    </motion.div>
    <AnimatePresence>
      {showFilm && toothrocket && <FilmViewer venture={toothrocket} onClose={() => setShowFilm(false)} />}
    </AnimatePresence>
  </motion.div>
  );
};

export const PhilosophyPanel = () => (
  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-10">
    <motion.div variants={rise} className="iv-eyebrow">— THE IDEVRA PHILOSOPHY</motion.div>
    <div className="max-w-3xl">
      <motion.h2 variants={rise} className="f-orbitron text-2xl sm:text-4xl font-semibold leading-snug text-[#94A3B8]">
        {IDEVRA.philosophy.line1}
      </motion.h2>
      <motion.h2 variants={rise} className="f-orbitron mt-4 text-2xl sm:text-4xl font-bold leading-snug text-[#0F172A]">
        IDEVRA is built around the ability to{" "}
        <span className="text-[#0284C7] drop-shadow-[0_0_15px_rgba(56,189,248,0.5)]">repeatedly create</span> products.
      </motion.h2>
    </div>
    <motion.div variants={rise} className="iv-glass max-w-2xl rounded-2xl p-6 sm:p-8">
      <p className="text-sm sm:text-base leading-relaxed text-[#475569]">{IDEVRA.philosophy.body}</p>
    </motion.div>
  </motion.div>
);

export const ModelPanel = () => (
  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
    <motion.div variants={rise} className="iv-eyebrow">— OUR MODEL</motion.div>
    <motion.h2 variants={rise} className="f-orbitron max-w-2xl text-xl sm:text-3xl font-bold text-[#0F172A]">
      {IDEVRA.model.lede}
    </motion.h2>
    <div className="grid gap-5 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
      <motion.div variants={rise} className="iv-glass rounded-2xl p-6 sm:p-8" data-testid="model-create-card">
        <div className="f-mono text-[10px] tracking-[0.3em] text-[#0284C7]">01 · IDEVRA</div>
        <h3 className="f-syncopate mt-3 text-sm sm:text-base tracking-[0.2em] text-[#0F172A]">{IDEVRA.model.create.title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-[#475569]">{IDEVRA.model.create.body}</p>
      </motion.div>
      <motion.div variants={rise} className="hidden md:flex items-center justify-center">
        <span className="iv-glass flex h-12 w-12 items-center justify-center rounded-full text-[#0284C7]">
          <ArrowRight size={20} />
        </span>
      </motion.div>
      <motion.div variants={rise} className="iv-glass rounded-2xl p-6 sm:p-8" data-testid="model-commercialize-card">
        <div className="f-mono text-[10px] tracking-[0.3em] text-[#0284C7]">02 · PORTFOLIO &amp; PARTNERS</div>
        <h3 className="f-syncopate mt-3 text-sm sm:text-base tracking-[0.2em] text-[#0F172A]">{IDEVRA.model.commercialize.title}</h3>
        <p className="mt-4 text-sm leading-relaxed text-[#475569]">{IDEVRA.model.commercialize.body}</p>
      </motion.div>
    </div>
    <motion.p variants={rise} className="max-w-3xl border-l-2 border-[#38BDF8] pl-4 text-[13px] leading-relaxed text-[#64748B]">
      {IDEVRA.model.outcome}
    </motion.p>
  </motion.div>
);

export const CoreAreasPanel = ({ focusIdx }) => (
  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
    <motion.div variants={rise} className="iv-eyebrow">— CORE AREAS · INNOVATION PORTFOLIO</motion.div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {IDEVRA.coreAreas.map((a, i) => {
        const Icon = AREA_ICONS[a.icon];
        const focused = i === focusIdx;
        return (
          <motion.div
            key={a.label}
            variants={rise}
            data-testid={`core-area-${i}`}
            className={`iv-glass rounded-xl p-4 transition-shadow duration-300 ${
              focused ? "shadow-[0_0_0_2px_rgba(56,189,248,0.6),0_0_24px_rgba(56,189,248,0.4)]" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <Icon size={19} strokeWidth={1.6} color={focused ? "#0284C7" : "#64748B"} />
              <span className="f-orbitron text-[10px] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className="mt-3 text-[12px] font-medium leading-snug text-[#0F172A]">{a.label}</div>
          </motion.div>
        );
      })}
    </div>
    <motion.p variants={rise} className="max-w-3xl text-[13px] italic leading-relaxed text-[#64748B]">
      {IDEVRA.coreAreasNote}
    </motion.p>
  </motion.div>
);

export const VisionPanel = () => (
  <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-8">
    <motion.div variants={rise} className="iv-eyebrow">— GLOBAL VISION</motion.div>
    <motion.h2 variants={rise} className="f-orbitron max-w-3xl text-2xl sm:text-4xl font-bold leading-snug text-[#0F172A]">
      {IDEVRA.vision.heading}
    </motion.h2>
    <motion.p variants={rise} className="max-w-2xl text-sm sm:text-base leading-relaxed text-[#475569]">
      {IDEVRA.vision.body}
    </motion.p>
    <motion.div variants={rise} className="iv-glass max-w-2xl rounded-2xl p-6 sm:p-8">
      <p className="f-orbitron text-base sm:text-lg font-semibold leading-relaxed text-[#0284C7]">
        {IDEVRA.vision.objective}
      </p>
    </motion.div>
    <motion.div variants={rise} className="flex flex-wrap items-center gap-4" data-testid="vision-founder">
      <span className="iv-chip">FOUNDER · <span className="text-[#0F172A]">SYED AMER</span></span>
      <span className="f-mono text-[10px] tracking-[0.3em] text-[#94A3B8]">
        WHERE IDEAS BECOME INTELLECTUAL PROPERTY.
      </span>
    </motion.div>
  </motion.div>
);
